/**
 * TanStack Query hooks for study-level DTA list and metrics.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import type { DTARecord } from "@/services/dtaApi"
import { RunStatus, SourceType, WorkflowStage } from "@/generated/sdk"
import { dtaApiService } from "@/services/dtaApi"
import { ingestionApiService, type IngestionRun } from "@/services/ingestionApi"
import { studyKeys } from "./useStudies"

export const studyDtaKeys = {
  all: (studyId: string | undefined) => [...studyKeys.all, studyId ?? null, "dtas"] as const,
  list: (studyId: string | undefined) => [...studyDtaKeys.all(studyId), "list"] as const,
  metrics: (studyId: string | undefined) => [...studyDtaKeys.all(studyId), "metrics"] as const,
}

/**
 * Fetches paginated DTAs for a study using cursor-based infinite query. Polls every 10s to pick up
 * new DTAs or status changes.
 */
export function useStudyDtasList(studyId: string | undefined) {
  return useInfiniteQuery({
    queryKey: studyDtaKeys.list(studyId),
    queryFn: ({ pageParam }) =>
      dtaApiService.getStudyDTAs(studyId!, { limit: 20, nextToken: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
    enabled: !!studyId,
    refetchInterval: 10_000,
  })
}

/**
 * Creates a new version (amendment) of a DTA. On success, invalidates the study's DTA list so the
 * table reflects the new draft version.
 */
export function useAmendDta(studyId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ dtaId, changeReason }: { dtaId: string; changeReason: string }) =>
      dtaApiService.createVersion(dtaId, { change_reason: changeReason, change_source: "manual" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studyDtaKeys.list(studyId) })
    },
  })
}

/**
 * Fetches run data for a study. Polls every 10s to keep run metrics fresh.
 */
export function useStudyRuns(studyId: string | undefined) {
  return useQuery({
    queryKey: studyDtaKeys.metrics(studyId),
    queryFn: () => ingestionApiService.listRuns(studyId!),
    enabled: !!studyId,
    refetchInterval: 10_000,
  })
}

interface StudyMetrics {
  approvedDTAs: number
  inProgressDTAs: number
  totalDTAs: number
  vendorCount: number
  passedRuns: number
  failedRuns: number
  totalRuns: number
  avgLatencyMs: number | null
  /**
   * DTA counts keyed by workflow stage (DRAFT/REVIEW/APPROVED/ACTIVE/INACTIVE).
   */
  stageCounts: Record<WorkflowStage, number>
}

/**
 * Derives study-level metrics from the already-cached DTA list and run data. No additional API
 * calls - reuses data from useStudyDtasList and useStudyRuns.
 */
export function useStudyMetrics(
  allDtas: DTARecord[],
  runsData: { runs?: IngestionRun[] } | undefined,
): StudyMetrics | null {
  return useMemo(() => {
    if (allDtas.length === 0 && !runsData) return null

    const runs = runsData?.runs || []

    // DTA metrics (derived from the already-loaded infinite query cache)
    const approvedDTAs = allDtas.filter((d) => d.workflow_stage === "ACTIVE").length
    const uniqueVendors = new Set(
      allDtas.filter((d) => d.source_type === SourceType.ThirdParty).map((d) => d.vendor_id),
    )

    const stageCounts: Record<WorkflowStage, number> = {
      [WorkflowStage.Draft]: 0,
      [WorkflowStage.Review]: 0,
      [WorkflowStage.Approved]: 0,
      [WorkflowStage.Active]: 0,
      [WorkflowStage.Inactive]: 0,
    }
    for (const dta of allDtas) {
      const stage = dta.workflow_stage
      if (stage in stageCounts) stageCounts[stage] += 1
    }

    // Run metrics (derived from the polling runs query). Passed/failed key off the terminal
    // RunStatus only — a Completed run may carry an errorMessage, so including `|| r.errorMessage`
    // in the failed count double-counts it (also passed) and breaks the
    // inProgress = total - passed - failed math in the health panel.
    const passedRuns = runs.filter((r) => r.status === RunStatus.Completed).length
    const failedRuns = runs.filter((r) => r.status === RunStatus.Failed).length
    const completedRuns = runs.filter((r) => r.status === RunStatus.Completed)
    const avgLatencyMs =
      completedRuns.length > 0
        ? completedRuns.reduce(
            (sum, r) => sum + (r.lastModifiedAt.getTime() - r.createdAt.getTime()),
            0,
          ) / completedRuns.length
        : null

    return {
      approvedDTAs,
      inProgressDTAs: allDtas.length - approvedDTAs,
      totalDTAs: allDtas.length,
      vendorCount: uniqueVendors.size,
      passedRuns,
      failedRuns,
      totalRuns: runs.length,
      avgLatencyMs,
      stageCounts,
    }
  }, [allDtas, runsData])
}
